import { createFileRoute, Navigate, Outlet, useLocation, useMatches, useSearch } from '@tanstack/react-router'
import { Suspense } from 'react'
import { resolveRouteAccess, type RouteRedirect } from '@/route-access'
import { UserMenu } from './-components/user-menu'
import { AppTopBar } from './-components/app-topbar'
import { AppBottomNav } from './-components/app-bottom-nav'
import { CompanyWelcomeDialog } from './-components/companies/welcome-dialog'
import { getChromeVariant } from './-components/app-layout-skeleton'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useAppStore } from '@/lib/app-store'
import { trpc } from '@/main'
import { TransitionOutlet } from '@/components/custom/route-transition/surface/transition-outlet'
import { useTopLevelSections } from './-components/use-top-level-sections'

function useIsEmbedded(): boolean {
  const search = useSearch({ strict: false })
  const fromStore = useAppStore(
    (state) => state.store.sdk || !!state.store.clientId || !!state.store.app || !!state.store.userToken,
  )
  return !!search.token || !!search.clientId || fromStore
}

export const Route = createFileRoute('/_app')({
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.employees.getMyEmployeeProfile.queryOptions())
    // The route-transition outlet (SectionPager here + the report pagers below) reads this flag via
    // useSuspenseQuery to pick its engine at SSR time; prefetch it so it's warm on both sides and the chooser
    // never suspends the document after hydration. See components/custom/route-transition/transition-outlet.
    context.queryClient.prefetchQuery(trpc.common.scrollTransitionsEnabled.queryOptions())
  },
  component: AppLayout,
})

function AppLayout() {
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // Single, central access guard for the whole private area (see route-access). Resolves the session user
  // (already loaded by the root) + the active route against one policy and redirects BEFORE the route's
  // auth/context-gated query mounts — so the gated query never fires, SSR never throws, and no route
  // component carries its own <Navigate>. getUser is read by the root above us, so this is a cache hit.
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const location = useLocation()
  // Inside a route-transition live preview (shadow router) the app chrome — topbar, framed <main> — already
  // exists in the real tree this preview renders inside; redrawing it would nest a second shell in the pane.
  // Render just the matched route (the section, or a nested report tab, strips further down). The access guard
  // is skipped too: the preview only mounts destinations the live app already deemed reachable, and a
  // <Navigate> here would break the pinned preview. See route-transition/route-preview.tsx.
  const { previewFrom } = Route.useRouteContext()
  if (previewFrom !== undefined) return <Outlet />
  const redirect = resolveRouteAccess({ routeId, user, href: location.href })
  if (redirect) return <AccessRedirect redirect={redirect} />

  const variant = getChromeVariant(routeId)
  if (variant === 'simple') return <SimpleShell />
  if (variant !== 'full') return <Outlet />
  return <FullChrome />
}

// Render the typed <Navigate> for a resolved access redirect. The discriminated union keeps each `to`
// a route-tree literal (so TanStack type-checks it), and only /login carries the return `redirect`.
function AccessRedirect({ redirect }: { redirect: RouteRedirect }) {
  if (redirect.to === '/login') return <Navigate to="/login" search={{ redirect: redirect.redirect }} replace />
  if (redirect.to === '/employees') return <Navigate to="/employees" replace />
  if (redirect.to === '/report') return <Navigate to="/report" replace />
  return <Navigate to={redirect.to} replace />
}

function FullChrome() {
  const embedded = useIsEmbedded()
  if (embedded) {
    return (
      <div className="mx-2 flex h-dvh flex-col items-center md:mx-4" data-app-layout>
        <main className="max-w-9xl m-2 w-full flex-1 overflow-auto rounded-t-xl rounded-b-md border md:m-4 print:m-0 print:overflow-visible print:rounded-none print:border-0">
          <Outlet />
        </main>
      </div>
    )
  }

  return (
    <div className="mx-2 flex h-dvh flex-col items-center md:mx-4" data-app-layout>
      <AppTopBar />
      <main className="max-w-9xl m-2 mt-0 w-full flex-1 overflow-auto rounded-t-xl rounded-b-md border md:m-4 md:mt-0 print:m-0 print:overflow-visible print:rounded-none print:border-0">
        <SectionPager />
      </main>
      {/* Mobile only: the section links live down here instead of in the topbar. In-flow sibling of <main>
          (the shell is h-dvh and never scrolls), so it's permanently on screen without overlaying content. */}
      <AppBottomNav />
      <Suspense fallback={null}>
        <CompanyWelcomeDialog />
      </Suspense>
    </div>
  )
}

// The app's ROOT scroll-snap surface: the viewer's top-level sections (Teams / Jobs / Offers / …) become
// swipeable full-width panes, and a section nav click glides straight there. LATERAL — the sections are
// PEERS, not levels of a hierarchy, so no depth parallax and no cast shadow. A single-section viewer gets a plain <Outlet/> (nothing to
// swipe). Only in the full (non-embedded) chrome; the embedded SDK shell keeps a plain outlet.
function SectionPager() {
  const sections = useTopLevelSections()
  if (sections.length <= 1) return <Outlet />
  // h-full (not flex-1): <main> is a block with a definite height, so the stage fills it and the sections'
  // own `h-full` cascades — the same contract the plain <Outlet/> had here.
  return <TransitionOutlet group={sections} kind="lateral" className="h-full" />
}

function SimpleShell() {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const embedded = useIsEmbedded()

  return (
    <>
      {user && !embedded && (
        <div className="flex justify-end px-4 pt-3 print:hidden">
          <UserMenu />
        </div>
      )}
      <Outlet />
    </>
  )
}
